import '@/styles/cms-workspace-blocks.css';
import { CmsWorkspaceLayout } from '@/cms/components/blocks/shared/cms-workspace-layout';
import { Skeleton } from '@/components/ui/feedback/status/skeleton';
import { cn } from '@/lib/ui/utils';
import type { WorkspaceStudioRenderModel } from '../contracts/server-model';
import { WorkspaceHeroCopy } from '../shared/workspace-hero-copy';
import { WorkspacePreviewMasonry } from '../tool-views/workspace-preview-masonry';
import { WorkspaceConversationLoading } from './workspace-conversation-loading';

export type WorkspaceLoadingSurface = 'canvas' | 'conversation';

function SkeletonBlock({
  className,
}: {
  className?: string;
}) {
  return (
    <Skeleton asChild>
      <span
        className={cn(
          'cms-block-workspace-workspace-1-workspace-skeleton__skeletonBlock',
          className
        )}
      />
    </Skeleton>
  );
}

export function WorkspaceParameterPanelSkeleton() {
  return (
    <div
      className="cms-block-workspace-workspace-1-workspace-skeleton__parameterPanel"
      aria-busy="true"
    >
      <div className="cms-block-workspace-workspace-1-workspace-skeleton__panelHeader">
        <div className="cms-block-workspace-workspace-1-workspace-skeleton__toolHeader">
          <div className="cms-block-workspace-workspace-1-workspace-skeleton__toolHeaderCopy">
            <SkeletonBlock className="cms-block-workspace-workspace-1-workspace-skeleton__skeletonKicker" />
            <SkeletonBlock className="cms-block-workspace-workspace-1-workspace-skeleton__skeletonPanelTitle" />
          </div>
          <SkeletonBlock className="cms-block-workspace-workspace-1-workspace-skeleton__skeletonIconButton" />
        </div>
      </div>

      <div className="cms-block-workspace-workspace-1-workspace-skeleton__panelBody">
        <div className="cms-block-workspace-workspace-1-workspace-skeleton__controlGroup">
          <SkeletonBlock className="cms-block-workspace-workspace-1-workspace-skeleton__skeletonControlLabel" />
          <div className="cms-block-workspace-workspace-1-workspace-skeleton__uploadStrip">
            <div className="cms-block-workspace-workspace-1-workspace-skeleton__uploadStripInner">
              <SkeletonBlock
                className={cn(
                  'cms-block-workspace-workspace-1-workspace-skeleton__uploadTile',
                  'cms-block-workspace-workspace-1-workspace-skeleton__skeletonUploadTile'
                )}
              />
            </div>
          </div>
        </div>

        <div className="cms-block-workspace-workspace-1-workspace-skeleton__paramList">
          {[0, 1].map((item) => (
            <div
              key={item}
              className="cms-block-workspace-workspace-1-workspace-skeleton__paramRow"
            >
              <div className="cms-block-workspace-workspace-1-workspace-skeleton__paramRowMain">
                <SkeletonBlock className="cms-block-workspace-workspace-1-workspace-skeleton__skeletonParamLabel" />
                <div className="cms-block-workspace-workspace-1-workspace-skeleton__paramControlWrap">
                  <SkeletonBlock className="cms-block-workspace-workspace-1-workspace-skeleton__skeletonParamControl" />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="cms-block-workspace-workspace-1-workspace-skeleton__panelFooter">
        <div className="cms-block-workspace-workspace-1-workspace-skeleton__creditsRow">
          <SkeletonBlock className="cms-block-workspace-workspace-1-workspace-skeleton__skeletonCreditsLabel" />
          <SkeletonBlock className="cms-block-workspace-workspace-1-workspace-skeleton__skeletonCreditsValue" />
        </div>
        <SkeletonBlock
          className={cn(
            'cms-block-workspace-workspace-1-workspace-skeleton__submitButton',
            'cms-block-workspace-workspace-1-workspace-skeleton__skeletonSubmit'
          )}
        />
      </div>
    </div>
  );
}

export function WorkspaceCanvasSkeleton() {
  return (
    <div
      className="cms-block-workspace-workspace-1-workspace-skeleton__canvasLoading"
      aria-busy="true"
      data-workspace-canvas-skeleton
    >
      <Skeleton className="cms-block-workspace-workspace-1-workspace-skeleton__canvasLoadingFrame" />
    </div>
  );
}

export function WorkspaceSurfaceSkeleton({
  loadingLabel,
  surface,
}: {
  loadingLabel?: string;
  surface: WorkspaceLoadingSurface;
}) {
  return surface === 'conversation' ? (
    <WorkspaceConversationLoading label={loadingLabel} />
  ) : (
    <WorkspaceCanvasSkeleton />
  );
}

export function WorkspaceStudioSkeleton({
  hero,
  markWorkspaceRoot = true,
  rootId = 'workspace',
  surface = 'canvas',
}: {
  hero?: WorkspaceStudioRenderModel['shell']['hero'];
  markWorkspaceRoot?: boolean;
  rootId?: string | null;
  surface?: WorkspaceLoadingSurface;
}) {
  return (
    <section
      id={rootId ?? undefined}
      className="cms-block-workspace-workspace-1-workspace-skeleton__section"
      data-cms-workspace-root={markWorkspaceRoot ? '' : undefined}
    >
      <CmsWorkspaceLayout
        panelClassName="cms-block-workspace-workspace-1-workspace-skeleton__panel"
        contentClassName="cms-block-workspace-workspace-1-workspace-skeleton__workspace"
        panel={<WorkspaceParameterPanelSkeleton />}
        content={
          <div
            className="cms-block-workspace-workspace-1-workspace-shell__workspaceCanvasArea"
            data-workspace-canvas-scroll-root=""
          >
            {hero ? (
              <div className="cms-block-workspace-workspace-1-workspace-showcase-layout__workspaceInner cms-block-workspace-workspace-1-workspace-showcase-layout__workspaceInnerDiscovery">
                <div className="cms-block-workspace-workspace-1-workspace-showcase-layout__workspaceDiscovery">
                  <div className="cms-block-workspace-workspace-1-workspace-showcase-layout__workspaceDiscoveryHero">
                    <WorkspaceHeroCopy hero={hero} />
                  </div>
                  <WorkspacePreviewMasonry images={hero.previewImages} />
                </div>
              </div>
            ) : (
              <WorkspaceSurfaceSkeleton surface={surface} />
            )}
          </div>
        }
      />
    </section>
  );
}
